/**
 * Reporting / export API endpoints (Phase 15)
 */

import { fetchRawApi } from '../core';

/**
 * Returns the raw course-progress CSV text. The export is audited server-side.
 * Uses fetchRawApi (not fetchApi) because the response is text/csv, not JSON.
 * Mirrors the gradebook/storage export pattern.
 */
export async function exportCourseProgressCsv(
  courseId: string,
  componentName: string = 'Unknown'
): Promise<string> {
  const response = await fetchRawApi(
    `/courses/${courseId}/progress/export`,
    { method: 'GET' },
    componentName
  );
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const body = await response.json();
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      /* swallow */
    }
    throw new Error(`[${componentName}] ${message}`);
  }
  return response.text();
}

export type FinanceReportKind =
  | 'monthly_receivables'
  | 'voided_entries'
  | 'payment_method_mix';

/**
 * Returns the raw aggregated finance report CSV text. The export is audited
 * server-side. Optional inclusive date range in Unix ms.
 */
export async function exportFinanceReportCsv(
  report: FinanceReportKind,
  params: { start_ms?: number; end_ms?: number } = {},
  componentName: string = 'Unknown'
): Promise<string> {
  const qs = new URLSearchParams();
  if (params.start_ms != null) qs.set('start_ms', String(params.start_ms));
  if (params.end_ms != null) qs.set('end_ms', String(params.end_ms));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  const response = await fetchRawApi(
    `/finance/reports/${report}/export${query}`,
    { method: 'GET' },
    componentName
  );
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const body = await response.json();
      if (body && typeof body.error === 'string') message = body.error;
    } catch {
      /* swallow */
    }
    throw new Error(`[${componentName}] ${message}`);
  }
  return response.text();
}
